import { Pin, PinOff } from "lucide-react";
import { formatMinutes } from "../lib/domain";
import { getCourseSectionColorStyle } from "../lib/scheduleColors";
import type { SectionListItem } from "../shared/schemas";

const dayLabels: Record<string, string> = {
  M: "Monday",
  T: "Tuesday",
  W: "Wednesday",
  R: "Thursday",
  F: "Friday",
  S: "Saturday",
  U: "Sunday",
};

const dayOrder: Record<string, number> = {
  M: 1,
  T: 2,
  W: 3,
  R: 4,
  F: 5,
  S: 6,
  U: 7,
};

export interface TimetableElementRow {
  section: SectionListItem;
  meeting: SectionListItem["meetings"][number];
  pinned?: boolean;
  highlighted?: boolean;
}

interface TimetableElementsTableProps {
  title: string;
  description?: string;
  rows: TimetableElementRow[];
  onSectionClick?: (sectionId: string) => void;
  onTogglePin?: (sectionId: string) => void;
}

function scheduleTypeLabel(scheduleType: string): string {
  const code = scheduleType.trim().toUpperCase() || "OTHER";
  if (code === "LEC") return "LEC - Lecture";
  if (code === "LAB") return "LAB - Lab";
  if (code === "TUT") return "TUT - Tutorial";
  if (code === "THS") return "THS - Thesis";
  return code;
}

export function TimetableElementsTable({
  title,
  description,
  rows,
  onSectionClick,
  onTogglePin,
}: TimetableElementsTableProps) {
  const sortedRows = [...rows].sort(
    (left, right) =>
      (dayOrder[left.meeting.dayCode] ?? 99) - (dayOrder[right.meeting.dayCode] ?? 99) ||
      left.meeting.startMinutes - right.meeting.startMinutes ||
      left.meeting.endMinutes - right.meeting.endMinutes ||
      left.section.subject.localeCompare(right.section.subject) ||
      left.section.courseNumber.localeCompare(right.section.courseNumber),
  );

  return (
    <section className="panel overflow-hidden">
      <div className="border-b border-slate-200 p-4">
        <h2 className="text-lg font-black text-slate-950">{title}</h2>
        {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1050px] text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3">Day</th>
              <th className="px-4 py-3">Time</th>
              <th className="px-4 py-3">Course</th>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Room</th>
              <th className="px-4 py-3">Instructor</th>
              <th className="px-4 py-3">Enrollment</th>
              <th className="px-4 py-3">Status</th>
              {onTogglePin && <th className="px-4 py-3">Pin</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {sortedRows.map(({ section, meeting, pinned, highlighted }) => {
              const course = `${section.subject} ${section.courseNumber}-${section.sectionCode}`;
              const sectionColorStyle = getCourseSectionColorStyle(section);
              return (
                <tr
                  key={`${section.id}-${meeting.id}`}
                  className={`${highlighted ? "bg-rose-50" : "bg-white"} hover:bg-slate-50`}
                >
                  <td className="px-4 py-3 font-semibold text-slate-700">
                    {dayLabels[meeting.dayCode] ?? meeting.dayCode}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                    {formatMinutes(meeting.startMinutes)}-{formatMinutes(meeting.endMinutes)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-start gap-2">
                      <span
                        className="mt-1 h-3 w-3 shrink-0 rounded-full border"
                        style={sectionColorStyle}
                        aria-hidden="true"
                      />
                      <div>
                        {onSectionClick ? (
                          <button
                            className="font-bold text-brand-700 hover:text-brand-900"
                            type="button"
                            onClick={() => onSectionClick(section.id)}
                          >
                            {course}
                          </button>
                        ) : (
                          <span className="font-bold text-slate-900">{course}</span>
                        )}
                        <p className="text-xs text-slate-400">CRN {section.crn}</p>
                      </div>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                    {scheduleTypeLabel(section.scheduleType)}
                  </td>
                  <td className="max-w-[260px] px-4 py-3 text-slate-600">
                    <span className="line-clamp-2">{section.sectionTitle}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    <span className="font-semibold">{meeting.room || "TBA"}</span>
                    <p className="text-xs text-slate-400">{meeting.campus || section.campus}</p>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{section.facultyName || "TBA"}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                    {section.actualEnrollment} / {section.maxEnrollment}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {highlighted && <span className="badge bg-rose-100 text-rose-700">Conflict</span>}
                      {pinned && <span className="badge bg-brand-50 text-brand-700">Pinned</span>}
                      {!highlighted && !pinned && <span className="text-xs text-slate-400">Visible</span>}
                    </div>
                  </td>
                  {onTogglePin && (
                    <td className="px-4 py-3">
                      <button
                        className="button-secondary px-3 py-2"
                        type="button"
                        onClick={() => onTogglePin(section.id)}
                      >
                        {pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
                        {pinned ? "Unpin" : "Pin"}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
